<template>
  <button
    data-testid="button-component"
    :type="type"
    :class="[
      $slots.icon
        ? $slots.icon && !label
          ? 'p-2.5'
          : 'flex space-x-2 px-6 py-2.5'
        : 'px-6 py-2.5',
      buttonClasses,
    ]"
    @click="emitClick"
    :disabled="disabled || isLoading"
    :aria-disabled="disabled.toString()"
    :aria-busy="isLoading.toString()"
    :aria-label="isLoading ? 'Loading' : label"
  >
    <slot name="icon">
    </slot>
    <span
      v-if="isLoading"
      class="absolute inset-0 flex items-center justify-center"
    >
      <span class="h-4 w-4 animate-spin rounded-full border-4 border-current border-t-transparent"
      ></span>
    </span>
    <span
      :class="
        isLoading ? 'invisible opacity-0' : 'visible font-bold opacity-100'
      "
      data-testid="button-label"
    >{{ label }}</span
    >
  </button>
</template>

<script>
/** Buttons help users initiate an action such as submitting a form or allow the user to navigate to another page. Buttons follow a visual hierarchy, allowing a user to quickly identify the most important actions. The RSC Digital Design System has four variations of standard buttons. These button styles are hierarchical and can be used for varying levels of importance. */
export default {
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: value =>
        ['primary', 'secondary', 'tertiary'].includes(value),
    },
    /** Text label communicates the action that will happen when the user interacts with the button */
    label: {
      type: String,
      default: 'Button',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'button',
      validator: value => ['button', 'submit', 'reset'].includes(value),
    },
  },
  computed: {
    buttonClasses() {
      const variantClasses = {
        primary: {
          base: [
            'bg-royal-blue-100',
            'border-royal-blue-100',
            'border-2',
            'text-white',
            'rounded-sm',
            'relative',
          ],
          interactive: [
            'hover:bg-royal-blue-160',
            'hover:border-royal-blue-160',
            'focus:bg-science-blue-100',
            'focus:border-science-blue-100',
          ],
        },
        secondary: {
          base: [
            'bg-white',
            'text-royal-blue-100',
            'border-royal-blue-100',
            'border-2',
            'rounded-sm',
            'relative',
          ],
          interactive: [
            'hover:text-royal-blue-160',
            'hover:border-royal-blue-160',
            'focus:text-science-blue-100',
            'focus:border-science-blue-100',
          ],
        },
        tertiary: {
          base: [
            'bg-transparent',
            'text-royal-blue-100',
            'rounded-sm',
            'relative',
          ],
          interactive: [
            'hover:text-royal-blue-160',
            'focus:text-science-blue-100',
            'underline',
          ],
        },
      }

      const baseClasses = variantClasses[this.variant]?.base || []
      const interactiveClasses
        = this.disabled || this.isLoading
          ? []
          : variantClasses[this.variant]?.interactive || []
      const disabledClasses
        = this.disabled || this.isLoading
          ? ['opacity-20', 'cursor-not-allowed']
          : []

      return [...baseClasses, ...interactiveClasses, ...disabledClasses].join(
        ' ',
      )
    },
  },
  methods: {
    emitClick(event) {
      if (this.disabled) {
        event.preventDefault()
        return
      }
      this.$emit('customClick', event)
    },
  },
  emits: ['customClick'],
}
</script>
